import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { ArticleCard } from "../components/support/ArticleCard";
import { HelpCircle, MessageSquare, Search, Users, MessageSquarePlus, Calendar, ChevronRight } from "lucide-react";
import { fetchTickets, createTicket, fetchArticles, fetchForumCategories, fetchForumPosts, fetchForumPostById, createForumPost, createForumReply } from "../store/actions/supportActions";
import { Link } from "react-router-dom";

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const KnowledgeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: ${({ theme }) => theme?.breakpoints?.md ?? 768}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ForumLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const ForumSidebar = styled.div`
  flex: 1 1 240px;
  max-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ForumMain = styled.div`
  flex: 1 1 400px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const GuidelineItem = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors?.border || "#e5e7eb"};
  &:last-child { border-bottom: none; }
`;

const GuidelineTitle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors?.text?.heading || "#111827"};
`;

const GuidelineContent = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  padding-bottom: 10px;
`;

export default function Support() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ticketId: _ticketParam, postId } = useParams();
  const { tickets, articles, forumCategories, forumPosts, forumPost, loading, error } = useSelector((state) => state.support);
  const [activeTab, setActiveTab] = useState("knowledge-base");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("medium");
  const [ticketStatusFilter, setTicketStatusFilter] = useState("");
  const [kbSearch, setKbSearch] = useState("");
  const [kbCategory, setKbCategory] = useState("");
  const [forumCategoryId, setForumCategoryId] = useState("");
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostBody, setNewPostBody] = useState("");
  const [newPostCategoryId, setNewPostCategoryId] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [forumSearchQuery, setForumSearchQuery] = useState("");
  const [forumSort, setForumSort] = useState("latest");
  const [guidelinesOpen, setGuidelinesOpen] = useState(null);

  useEffect(() => {
    dispatch(fetchTickets(ticketStatusFilter ? { status: ticketStatusFilter } : {}));
  }, [dispatch, ticketStatusFilter]);

  useEffect(() => {
    if (location.pathname.includes("/forum/")) {
      setActiveTab("community");
    }
  }, [location]);

  useEffect(() => {
    if (activeTab === "knowledge-base") {
      const params = {};
      if (kbSearch.trim()) params.search = kbSearch.trim();
      if (kbCategory) params.category = kbCategory;
      dispatch(fetchArticles(params));
    }
  }, [dispatch, activeTab, kbSearch, kbCategory]);

  useEffect(() => {
    if (activeTab === "community" && !postId) {
      dispatch(fetchForumCategories());
    }
  }, [dispatch, activeTab, postId]);

  useEffect(() => {
    if (activeTab === "community" && !postId) {
      dispatch(fetchForumPosts(forumCategoryId ? { categoryId: forumCategoryId } : {}));
    }
  }, [dispatch, activeTab, forumCategoryId, postId]);

  useEffect(() => {
    if (postId) {
      dispatch(fetchForumPostById(postId));
    }
  }, [dispatch, postId]);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    const result = await dispatch(createTicket({ subject: subject.trim(), message: message.trim(), priority }));
    if (result?.success) {
      setSubject("");
      setMessage("");
      setActiveTab("contact");
    }
  };

  const isForumPostPage = !!postId;

  const handleNewPost = async (e) => {
    e.preventDefault();
    if (!newPostTitle.trim() || !newPostBody.trim() || !newPostCategoryId) return;
    const result = await dispatch(createForumPost({ title: newPostTitle.trim(), body: newPostBody.trim(), categoryId: newPostCategoryId }));
    if (result?.success) {
      setNewPostTitle("");
      setNewPostBody("");
      setNewPostCategoryId("");
      setShowNewPostModal(false);
      dispatch(fetchForumPosts(forumCategoryId ? { categoryId: forumCategoryId } : {}));
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !postId) return;
    const result = await dispatch(createForumReply(postId, { body: replyBody.trim() }));
    if (result?.success) setReplyBody("");
  };

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title={t("support.title")}
        sub={t("support.subtitle")}
      />

      <CardsGrid>
        <Card style={{ padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <Search size={20} />
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>{t("support.knowledgeBase")}</h3>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
            {t("support.browseDocumentation")}
          </p>
        </Card>

        <Card style={{ padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <HelpCircle size={20} />
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>{t("support.helpCenter")}</h3>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
            {t("support.findAnswers")}
          </p>
        </Card>

        <Card style={{ padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <MessageSquare size={20} />
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>{t("support.contactSupport")}</h3>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
            {t("support.getInTouch")}
          </p>
        </Card>

        <Card style={{ padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <Users size={20} />
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>{t("support.communityForum")}</h3>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
            {t("support.connectWithOthers")}
          </p>
        </Card>
      </CardsGrid>

      <div style={{ marginTop: 32 }}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="knowledge-base">{t("support.knowledgeBaseTab")}</TabsTrigger>
          <TabsTrigger value="community">{t("support.communityForumTab")}</TabsTrigger>
          <TabsTrigger value="contact">{t("support.contactSupportTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="knowledge-base">
          <div style={{ marginTop: 24 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20, alignItems: "center" }}>
              <div style={{ position: "relative" }}>
                <Search size={16} color="#6b7280" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                <Input placeholder={t("support.searchArticles")} value={kbSearch} onChange={(e) => setKbSearch(e.target.value)} style={{ paddingLeft: 34, minWidth: 220 }} />
              </div>
              <select value={kbCategory} onChange={(e) => setKbCategory(e.target.value)} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db" }}>
                <option value="">{t("support.allCategories")}</option>
                <option value="Setup">Setup</option>
                <option value="Orders">Orders</option>
                <option value="Marketing">Marketing</option>
                <option value="Account">Account</option>
                <option value="Billing">Billing</option>
                <option value="Integrations">Integrations</option>
                <option value="General">General</option>
              </select>
            </div>
            {loading && articles.length === 0 ? (
              <Card style={{ padding: 32, textAlign: "center" }}>
                <p style={{ margin: 0, color: "#6b7280" }}>{t("support.loadingArticles")}</p>
              </Card>
            ) : error && articles.length === 0 ? (
              <Card style={{ padding: 32, textAlign: "center" }}>
                <p style={{ margin: 0, color: "#dc2626" }}>{error}</p>
              </Card>
            ) : articles.length === 0 ? (
              <Card style={{ padding: 32, textAlign: "center" }}>
                <p style={{ margin: 0, color: "#6b7280" }}>{t("support.noArticlesFound")}</p>
              </Card>
            ) : (
              <KnowledgeGrid>
                {articles.map((article) => (
                  <ArticleCard
                    key={article._id}
                    title={article.title}
                    description={article.description || ""}
                    category={article.category || "General"}
                    onClick={() => navigate(`/support/article/${article._id}`)}
                  />
                ))}
              </KnowledgeGrid>
            )}
          </div>
        </TabsContent>

        <TabsContent value="community">
          <div style={{ marginTop: 24 }}>
            {isForumPostPage ? (
              <>
                <Button $variant="ghost" $size="sm" style={{ marginBottom: 16 }} onClick={() => navigate("/support")}>
                  ← {t("support.backToForum")}
                </Button>
                {loading && !forumPost ? (
                  <Card style={{ padding: 24 }}><p style={{ color: "#6b7280" }}>{t("support.loading")}</p></Card>
                ) : error && !forumPost ? (
                  <Card style={{ padding: 24 }}><p style={{ color: "#dc2626" }}>{error}</p></Card>
                ) : forumPost ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                    <Card style={{ padding: 24, borderLeft: forumPost.pinned ? "4px solid #6366f1" : undefined }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                        <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, background: "#f3f4f6", color: "#374151" }}>{forumPost.category || "General"}</span>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>{forumPost.createdAt ? new Date(forumPost.createdAt).toLocaleDateString() : ""}</span>
                        {forumPost.pinned && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "rgba(99, 102, 241, 0.15)", color: "#6366f1" }}>{t("support.pinned")}</span>}
                      </div>
                      <h2 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 600 }}>{forumPost.title}</h2>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: "#374151" }}>
                          {(forumPost.author || "?").charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>{forumPost.author || "Unknown"}</span>
                      </div>
                      <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: 15 }}>{forumPost.body}</div>
                    </Card>

                    <div>
                      <h3 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                        <MessageSquare size={20} />
                        {t("support.replies")} ({(forumPost.replies || []).length})
                      </h3>
                      {(forumPost.replies || []).length === 0 ? (
                        <Card style={{ padding: 24, textAlign: "center" }}>
                          <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("support.noRepliesYet")}</p>
                        </Card>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {(forumPost.replies || []).map((r) => (
                            <Card key={r._id} style={{ padding: 16 }}>
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: "#374151", flexShrink: 0 }}>{(r.author || "?").charAt(0).toUpperCase()}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontWeight: 600, fontSize: 14 }}>{r.author || "Unknown"}</span>
                                  <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}</span>
                                </div>
                              </div>
                              <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5, paddingLeft: 40 }}>{r.body}</div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>

                    {!forumPost.locked && (
                      <Card style={{ padding: 24 }}>
                        <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 600 }}>{t("support.addYourReply")}</h3>
                        <p style={{ margin: "0 0 16px", fontSize: 14, color: "#6b7280" }}>{t("support.shareKnowledge")}</p>
                        <form onSubmit={handleReply}>
                          <textarea placeholder={t("support.typeReplyPlaceholder")} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={4} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", marginBottom: 12, fontSize: 14 }} />
                          <Button type="submit" disabled={loading || !replyBody.trim()}>
                            <MessageSquare size={14} style={{ marginRight: 6 }} />
                            {t("support.postReply")}
                          </Button>
                        </form>
                      </Card>
                    )}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <ForumLayout>
                  <ForumSidebar>
                    <Card style={{ padding: 16 }}>
                      <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600 }}>{t("support.categories")}</h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <button type="button" onClick={() => setForumCategoryId("")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 6, border: "none", background: forumCategoryId === "" ? "rgba(99, 102, 241, 0.1)" : "transparent", fontSize: 14, fontWeight: forumCategoryId === "" ? 600 : 400, color: "#374151", cursor: "pointer", textAlign: "left" }}>
                          <span>{t("support.allTopics")}</span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>{(forumPosts || []).length}</span>
                        </button>
                        {(forumCategories || []).map((cat) => (
                          <button key={cat._id || cat.id} type="button" onClick={() => setForumCategoryId(cat._id || cat.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 6, border: "none", background: forumCategoryId === (cat._id || cat.id) ? "rgba(99, 102, 241, 0.1)" : "transparent", fontSize: 14, fontWeight: forumCategoryId === (cat._id || cat.id) ? 600 : 400, color: "#374151", cursor: "pointer", textAlign: "left" }}>
                            <span>{cat.name}</span>
                            <span style={{ fontSize: 12, color: "#6b7280" }}>{cat.count ?? 0}</span>
                          </button>
                        ))}
                      </div>
                    </Card>
                    <Card style={{ padding: 16 }}>
                      <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600 }}>{t("support.forumGuidelines")}</h3>
                      {[
                        { titleKey: "support.guidelineRespectful", contentKey: "support.guidelineRespectfulContent" },
                        { titleKey: "support.guidelineOnTopic", contentKey: "support.guidelineOnTopicContent" },
                        { titleKey: "support.guidelineNoPromotions", contentKey: "support.guidelineNoPromotionsContent" },
                        { titleKey: "support.guidelineSearchFirst", contentKey: "support.guidelineSearchFirstContent" },
                      ].map((g, i) => (
                        <GuidelineItem key={i}>
                          <GuidelineTitle type="button" onClick={() => setGuidelinesOpen(guidelinesOpen === i ? null : i)}>
                            {t(g.titleKey)}
                            <span style={{ transform: guidelinesOpen === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
                          </GuidelineTitle>
                          {guidelinesOpen === i && <GuidelineContent>{t(g.contentKey)}</GuidelineContent>}
                        </GuidelineItem>
                      ))}
                    </Card>
                  </ForumSidebar>
                  <ForumMain>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                      <div>
                        <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700 }}>{t("support.communityForumHeading")}</h2>
                        <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>{t("support.connectAndShare")}</p>
                      </div>
                      <Button onClick={() => setShowNewPostModal(true)}>
                        <MessageSquarePlus size={16} style={{ marginRight: 6 }} />
                        {t("support.newPost")}
                      </Button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                      <div style={{ position: "relative", flex: "1 1 200px", minWidth: 180 }}>
                        <Search size={16} color="#6b7280" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                        <Input placeholder={t("support.searchDiscussions")} value={forumSearchQuery} onChange={(e) => setForumSearchQuery(e.target.value)} style={{ paddingLeft: 34, width: "100%" }} />
                      </div>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button type="button" onClick={() => setForumSort("latest")} style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 12px", borderRadius: 8, border: "none", background: forumSort === "latest" ? "rgba(99, 102, 241, 0.15)" : "#f3f4f6", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                          <Calendar size={14} /> {t("support.latest")}
                        </button>
                        <button type="button" onClick={() => setForumSort("mostReplies")} style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 12px", borderRadius: 8, border: "none", background: forumSort === "mostReplies" ? "rgba(99, 102, 241, 0.15)" : "#f3f4f6", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                          <MessageSquare size={14} /> {t("support.mostReplies")}
                        </button>
                      </div>
                    </div>
                    {loading && (forumPosts || []).length === 0 ? (
                      <Card style={{ padding: 32, textAlign: "center" }}><p style={{ margin: 0, color: "#6b7280" }}>{t("support.loading")}</p></Card>
                    ) : (() => {
                      const q = forumSearchQuery.trim().toLowerCase();
                      const filtered = (forumPosts || []).filter((p) => !q || (p.title || "").toLowerCase().includes(q) || (p.body || "").toLowerCase().includes(q));
                      const sorted = [...filtered].sort((a, b) => {
                        if (a.pinned && !b.pinned) return -1;
                        if (!a.pinned && b.pinned) return 1;
                        if (forumSort === "mostReplies") return (b.replies ?? 0) - (a.replies ?? 0);
                        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                      });
                      if (sorted.length === 0) {
                        return (
                          <Card style={{ padding: 32, textAlign: "center" }}>
                            <MessageSquare size={40} style={{ color: "#9ca3af", marginBottom: 12 }} />
                            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>{t("support.noDiscussionsFound")}</h3>
                            <p style={{ margin: 0, color: "#6b7280", marginBottom: 16 }}>{t("support.tryDifferentFilters")}</p>
                            <Button onClick={() => { setForumSearchQuery(""); setForumCategoryId(""); }}>{t("support.clearFilters")}</Button>
                          </Card>
                        );
                      }
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {sorted.map((post) => (
                            <Card
                              key={post._id || post.id}
                              style={{
                                padding: 16,
                                cursor: "pointer",
                                borderLeft: post.pinned ? "4px solid #6366f1" : undefined,
                                transition: "box-shadow 0.2s, border-color 0.2s",
                              }}
                              onClick={() => navigate(`/support/forum/${post._id || post.id}`)}
                              onKeyDown={(e) => e.key === "Enter" && navigate(`/support/forum/${post._id || post.id}`)}
                              role="button"
                              tabIndex={0}
                              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                                    {post.pinned && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "rgba(99, 102, 241, 0.15)", color: "#6366f1" }}>{t("support.pinned")}</span>}
                                    <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 6, background: "#f3f4f6", color: "#374151" }}>{post.category || "General"}</span>
                                    <span style={{ fontSize: 12, color: "#6b7280" }}>{post.createdAt ? new Date(post.createdAt).toLocaleDateString() : ""}</span>
                                  </div>
                                  <h3 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 600 }}>{post.title}</h3>
                                  {post.body && <p style={{ margin: 0, fontSize: 14, color: "#6b7280", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{post.body}</p>}
                                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: "#374151" }}>{(post.author || "?").charAt(0).toUpperCase()}</div>
                                      <span style={{ fontSize: 13, color: "#6b7280" }}>{post.author || "Unknown"}</span>
                                    </div>
                                    <span style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
                                      <MessageSquare size={14} /> {post.replies ?? 0} replies
                                    </span>
                                  </div>
                                </div>
                                <ChevronRight size={20} style={{ color: "#9ca3af", flexShrink: 0 }} />
                              </div>
                            </Card>
                          ))}
                        </div>
                      );
                    })()}
                  </ForumMain>
                </ForumLayout>
              </>
            )}
          </div>
          {showNewPostModal && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={() => setShowNewPostModal(false)}>
              <div style={{ background: "#fff", borderRadius: 12, padding: 24, maxWidth: 480, width: "90%" }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600 }}>New post</h3>
                <form onSubmit={handleNewPost} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Input placeholder="Title" value={newPostTitle} onChange={(e) => setNewPostTitle(e.target.value)} />
                  <select value={newPostCategoryId} onChange={(e) => setNewPostCategoryId(e.target.value)} required style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db" }}>
                    <option value="">Select category</option>
                    {(forumCategories || []).map((cat) => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <textarea placeholder="Body" value={newPostBody} onChange={(e) => setNewPostBody(e.target.value)} rows={4} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db" }} />
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Button type="button" $variant="outline" onClick={() => setShowNewPostModal(false)}>Cancel</Button>
                    <Button type="submit" disabled={loading || !newPostTitle.trim() || !newPostBody.trim() || !newPostCategoryId}>Create post</Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="contact">
          <Card style={{ marginTop: 24, padding: "20px" }}>
            <h3 style={{ margin: "0 0 12px", fontSize: "18px", fontWeight: 600 }}>Contact Support</h3>
            {error && <p style={{ color: "#dc2626", marginBottom: "12px" }}>{error}</p>}
            <form onSubmit={handleSubmitTicket} style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "400px" }}>
              <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "#6b7280", marginBottom: 4, display: "block" }}>Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db" }}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <textarea placeholder="Message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db" }} />
              <Button type="submit" disabled={loading}>Submit ticket</Button>
            </form>
            {tickets.length > 0 && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 24, marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Your tickets</h4>
                  <select value={ticketStatusFilter} onChange={(e) => setTicketStatusFilter(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db" }}>
                    <option value="">All</option>
                    <option value="open">Open</option>
                    <option value="in-progress">In progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {tickets.map((t) => (
                    <li key={t._id} style={{ marginBottom: 8 }}>
                      <Link to={`/support/ticket/${t._id}`} style={{ textDecoration: "none", color: "inherit", display: "block", padding: 12, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fafafa" }}>
                        <span style={{ fontWeight: 600 }}>{t.subject || t.title}</span>
                        <span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>{t.status || "open"}</span>
                        {t.priority && <span style={{ marginLeft: 6, fontSize: 12, color: "#6b7280" }}> | {t.priority}</span>}
                        {t.createdAt && <span style={{ marginLeft: 6, fontSize: 12, color: "#9ca3af" }}> | {new Date(t.createdAt).toLocaleDateString()}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
